<template>
  <div>
    <div 
      :class="['tools', toolbarPosition, hide ? 'hide' : '']" 
      :style="{
        'transform': `translateY(${toolbarRect.top}px)`
      }"
      ref="toolbar"
    >
      <div 
        :class="['tools__toggle-btn tools__btn', light ? 'tools__light--off' : 'tools__light--on']" 
        @click="toggleLight">
        <magnifier-light />
      </div>
      <div @click="magnify(1)" 
        :class="['tools__btn tools__btn--magnify', scale === 1 ? 'selected' : '']">
        1.0X
      </div>
      <div @click="magnify(1.5)" 
        :class="['tools__btn tools__btn--magnify', scale === 1.5 ? 'selected' : '']">
        1.5X
      </div>
      <div @click="magnify(2)" 
        :class="['tools__btn tools__btn--magnify', scale === 2 ? 'selected' : '']">
        2.0X
      </div>
      <div @click="closeMagnifier" class="tools__btn--close">关闭</div>
    </div>

    <div v-for="elem in fakeToolbarElems" :key="elem" ref="fakeToolbarElems"
      :class="['tools', 'fake', elem]"
      :data-elem="elem"
    >
      <div class="tools__btn"></div>
      <div class="tools__btn">1.0X</div>
      <div class="tools__btn">1.5X</div>
      <div class="tools__btn">2.0X</div>
      <div class="tools__btn--close">关闭</div>
    </div>
  </div>
</template>

<script>
import light from './light.vue';
import { IntersectionObserverPolyfill } from './intersection-observer';

export default {
  props: ['rect', 'hide', 'light', 'scale', 'shape'],
  components: {
    'magnifier-light': light,
  },
  data() {
    const fakeToolbarElems = ['right', 'bottom', 'left', 'top'];
    const fakeToolbarElemIntersecting = fakeToolbarElems.reduce((acc, elem) => {
      acc[elem] = false;
      return acc;
    }, {});

    return {
      fakeToolbarElems,
      fakeToolbarElemIntersecting,
      toolbarPosition: 'right',
      toolbarRect: {
        top: 0
      },
    };
  },
  mounted() {
    const root = document.body;

    const fakeToolbarObserver = new IntersectionObserverPolyfill((entries) => {
      entries.forEach(({ target, intersectionRatio, }) => {
        const elem = target.getAttribute('data-elem');

        this.fakeToolbarElemIntersecting[elem] = intersectionRatio < 1;

        const pos = this.findAvailablePosition();
        if (pos) {
          this.resetToolbar(pos);
        }     
      });
    }, {
      root,
      rootMargin: '-6px',
      threshold: [1],
    });

    for (let i = 0; i < this.fakeToolbarElems.length; i++) {
      fakeToolbarObserver.observe(this.$refs.fakeToolbarElems[i]);
    }

    const threshold = [];

    for (let i = 0; i <= 1; i += 0.1) {
      threshold.push(i === 1 ? 0.99 : i);
    }

    const options = {
      root,
      rootMargin: '0px',
      threshold,
    };

    const toolbarObserver = new IntersectionObserverPolyfill((entries) => {
      this.setToolbarPosition(entries[0]);
    }, options);
    toolbarObserver.observe(this.$refs.toolbar);

    this.$once('hook:beforeDestroy', () => {
      fakeToolbarObserver.disconnect();
      toolbarObserver.disconnect();
    });
  },
  methods: {
    changeShape() {
      const shape = this.shape === 'circle' ? 'rect' : 'circle';
      this.$emit('update:shape', shape);

      this.$flydata.flydataTrack(shape === 'circle' ? 268 : 269);
    },
    toggleLight() {
      const light = !this.light;
      this.$emit('update:light', light);

      this.$flydata.flydataTrack(263, {
        light_status: light ? 'on' : 'off'
      });
    },
    closeMagnifier() {
      this.$emit('close');
    },
    magnify(scale) {
      this.$emit('update:scale', scale);

      switch(scale) {
        case 1:
          this.$flydata.flydataTrack(272);
          break;

        case 1.5:
          this.$flydata.flydataTrack(271);
          break;

        case 2:
          this.$flydata.flydataTrack(270);
          break;
      }
    },
    resetToolbar(position = 'right') {
      this.toolbarPosition = position;
      this.toolbarRect.top = 0;
    },
    findAvailablePosition() {
      const positions = this.fakeToolbarElems.filter((pos) => {
        return !this.fakeToolbarElemIntersecting[pos];
      });

      return positions.length > 0 ? positions[0] : null;
    },
    setToolbarPosition({ intersectionRect, boundingClientRect, intersectionRatio }) {
      if (intersectionRatio < 1) {
        const { top, left, width, height } = intersectionRect;

        if (top <= 0) {
          const pos = this.findAvailablePosition();

          if (pos) {
            this.resetToolbar(pos);
          } else {
            const isLeft = this.toolbarPosition === 'left';
            const position = [isLeft ? 'left' : 'right', 'align-top'];
            this.toolbarPosition = position.join(' ');

            const calcTop = () => -this.rect.top;
            this.toolbarRect.top = calcTop();

            const unwatch = this.$watch('rect.top', () => {
              const pos = this.findAvailablePosition();

              if (pos) {
                this.resetToolbar(pos);
                unwatch();
              } else {
                this.toolbarRect.top = calcTop();
              }
            });
          }

          return;
        }

        if (left <= 0) {
          const pos = this.findAvailablePosition();

          if (pos) {
            this.resetToolbar(pos);
          } else {
            const isTop = this.toolbarPosition === 'top';
            const position = ['right', isTop ? 'align-bottom' : 'align-top'];
            this.toolbarPosition = position.join(' ');

            const calcTop = () => {
              return isTop ? window.innerHeight - this.rect.top - this.rect.height : -this.rect.top;
            };
            this.toolbarRect.top = calcTop();

            const unwatch = this.$watch('rect.top', () => {
              const pos = this.findAvailablePosition();

              if (pos) {
                this.resetToolbar(pos);
                unwatch();
              } else {
                this.toolbarRect.top = calcTop();
              }
            });
          }

          return;
        }

        if (width < boundingClientRect.width) {
          const pos = this.findAvailablePosition();

          if (pos) {
            this.resetToolbar(pos);
          } else {
            const isTop = this.toolbarPosition === 'top';
            const position = ['left', isTop ? 'align-bottom' : 'align-top'];
            this.toolbarPosition = position.join(' ');

            const calcTop = () => {
              return isTop ? window.innerHeight - this.rect.top - this.rect.height : -this.rect.top;
            };
            this.toolbarRect.top = calcTop();

            const unwatch = this.$watch('rect.top', () => {
              const pos = this.findAvailablePosition();

              if (pos) {
                this.resetToolbar(pos);
                unwatch();
              } else {
                this.toolbarRect.top = calcTop();
              }
            });
          }
          return;
        }

        if (height < boundingClientRect.height) {
          const pos = this.findAvailablePosition();

          if (pos) {
            this.resetToolbar(pos);
          } else {
            const isLeft = this.toolbarPosition === 'left';
            const position = [isLeft ? 'left' : 'right', 'align-bottom'];
            this.toolbarPosition = position.join(' ');

            const calcTop = () => {
              return window.innerHeight - this.rect.top - this.rect.height;
            };
            this.toolbarRect.top = calcTop();

            const unwatch = this.$watch('rect.top', () => {
              const pos = this.findAvailablePosition();

              if (pos) {
                this.resetToolbar(pos);
                unwatch();
              } else {
                this.toolbarRect.top = calcTop();
              }
            });
          }

          return;
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tools {
  position: absolute;
  width: 72px;
  padding: 20px 12px 0 12px;
  border-radius: 10px;
  background-color: #fff;

  &.hide {
    visibility: hidden;
  }

  &.fake {
    z-index: -1;
    visibility: hidden;
  }

  &.top,
  &.bottom {
    display: flex;
    width: auto;
    height: 72px;
    padding: 12px 0 12px 20px;

    .tools__btn {
      margin-bottom: 0;
      margin-right: 20px;
    }

    .tools__btn--close {
      width: 63px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-top: none;
      border-left: 1px solid #DADEE6;
    }
  }

  &.top {
    top: -104px;
    right: 0;
  }

  &.bottom {
    bottom: -104px;
    right: 0;
  }

  &.left,
  &.right {
    &.align-top {
      top: 0;
      bottom: initial;
    }

    &.align-bottom {
      bottom: 0;
    }
  }

  &.left {
    left: -104px;
    bottom: 0;
  }

  &.right {
    right: -104px;
    bottom: 0;
  }
}

.tools__btn {
  width: 48px;
  height: 48px;
  border-radius: 20px;
  margin-bottom: 20px;
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;

  &.circle {
    background-color: #FFCE00;
  }

  &.rect {
    background-color: #FFA700;
  }
}

.tools__btn--magnify {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  color: #222222;
  background-color: #DADEE6;

  &.selected {
    color: #FFFFFF;
    background-color: #037BFE;
  }
}

.tools__btn--close {
  border-top: 1px solid #DADEE6;
  padding: 12px 0;
  font-size: 16px;
  font-weight: 500;
  color: #586D94;
  text-align: center;
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;
}

.tools__toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tools__light--on {
  color: #fff;
  background-color: #FFA700;
}

.tools__light--off {
  color: #586D94;
  background-color: #DADEE6;
}
</style>